import { useEffect, useContext } from 'react';
import { NavigationContext } from '../../context/NavigationContext';
import { useFormState } from 'react-final-form';
import { Display, DisplayLink } from 'shared/components';
import { PreviousStepButton } from 'shared/buttons';
import { Heading, LabelText, Collapsable } from 'shared/components';
import { currencyFormatter, percentageFormatter } from '@dnhb/formatters';
import { MONTHS_OPTIONS, shapeSettings } from '../../config';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import {InputContainer} from 'shared/wizard/ToolContainer';
import { useNavigate } from 'react-router-dom';

const Seperator = styled.hr(({theme}) => css`
  border: 0;
  border-top: 1px dashed ${theme.borderColor};
  height: 1px;
  margin-bottom: 16px;
`);

const LoanpartHeading = styled.h5(({theme}) => css`
  font-family: ${theme.fontFamily};
  color: ${theme.primaryColor};
  font-size: 14px;
  line-height: 22px;
  margin-bottom: 12px;
  font-weight: 800;
`);

const OverviewContainer = ({
  settings,
  applicant_income,
  applicant_pension_income,
  partner_income,
  partner_pension_income,
}) => {
  const navigate = useNavigate();
  useEffect(() => {
    navigate('/hypotheekbespaarcheck/overview');
  }, [navigate]);

  const {
    currentStepIndex,
    setSelectedDetailType,
    updateStepIndex,
    setRecalculatingFromIndex,
    flow,
  } = useContext(NavigationContext);

  const formState = useFormState();
  const {
    original_mortgage_debt,
    original_house_value,
    house_purchase_year,
    current_house_value_unknown,
    current_mortgage_debt,
    current_house_value,
    postal_code,
    estimated_current_house_value,
    interest_rate_unknown,
    interest_rate,
    estimated_interest_rate,
    variable_interest_rate,
    fixed_rate_term_end_unknown,
    fixed_rate_term_end_month,
    fixed_rate_term_end_year,
    estimated_fixed_rate_term_end,
    mortgage_provider,
    product_line,
    nhg,
    loanparts,
    applicant_dob,
    applicant_showPension,
    applicant_is_entrepreneur,
    applicant_has_partner,
    partner_dob,
    partner_showPension,
    partner_is_entrepreneur,
  } = formState.values;

  const isOnFinalPage = currentStepIndex === flow.length - 1;
    
  const setStepIndex = (i) => {
    setSelectedDetailType(null);
    setRecalculatingFromIndex(currentStepIndex);
    updateStepIndex(i);
  };
  const provider = settings.meta.mortgage_providers
    .find(provider => provider.value === mortgage_provider)

  return (
    <InputContainer>
      <Heading>Uitgangspunten</Heading>
      <LabelText css={{ marginBottom: '24px' }}>
        Controleer of jouw gegevens juist zijn, pas deze zo nodig aan om de juiste berekening te maken.
      </LabelText>
      <Collapsable title="Oorspronkelijke situatie" openByDefault={!isOnFinalPage}>
        <Display
          label="Hypotheek"
          value={currencyFormatter(original_mortgage_debt)}
        />
        <Display
          label="Woningwaarde"
          value={currencyFormatter(original_house_value)}
        />
        <Display
          label="Koopjaar"
          value={house_purchase_year}
        />
        <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(0)} />
      </Collapsable>
      <Collapsable title="Huidige situatie">
        {!isOnFinalPage && (
          <Display
            label="Hypotheek"
            value={currencyFormatter(current_mortgage_debt)}
          />
        )}
        {current_house_value_unknown ? (
          <>
            <Display
              label="Postcode"
              value={postal_code}
            />
            <Display
              label="Geschatte woningwaarde"
              value={currencyFormatter(estimated_current_house_value, 0)}
            />
          </>
        ) : (
          <Display
            label="Woningwaarde"
            value={currencyFormatter(current_house_value)}
          />
        )}
        <DisplayLink css={css`margin-top: 16px; `} onClick={() => {
          setStepIndex(1);
        }} />
      </Collapsable>
      {!isOnFinalPage && (
        <Collapsable title="Hypotheekrente">
          {interest_rate_unknown ? (
            <Display
              label="Geschatte rente"
              value={estimated_interest_rate
                .split('-')
                .map(Number)
                .map(value => percentageFormatter(value, 0))
                .join(' tot ')
              }
            />
          ) : (
            <Display
              label="Rente"
              value={percentageFormatter(interest_rate, 2)}
            />
          )}
          <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(2)} />
        </Collapsable>
      )}
      {!isOnFinalPage && (
        <Collapsable title={variable_interest_rate ? "Variabele rente" : "Rentevaste periode"}>
          {variable_interest_rate ? (
            <Display
              label="Variabele rente"
              value={variable_interest_rate}
            />
          ) : (
            <Display
              label="Einde rentevaste periode"
              value={fixed_rate_term_end_unknown
                ? `Binnen ${Math.round(estimated_fixed_rate_term_end / 12)} jaar`
                : `${MONTHS_OPTIONS[fixed_rate_term_end_month-1].label} ${fixed_rate_term_end_year}`
              }
            />
          )}
          <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(3)} />
        </Collapsable>
      )}
      <Collapsable title="Hypotheekverstrekker">
        <Display
          label="Verstrekker"
          value={provider.label}
        />
        {provider.labels.length > 2 && (
          <Display
            label="Label"
            value={provider.labels.find(label => label.value === product_line).label}
          />
        )}
        <Display
          label="NHG"
          value={nhg}
        />
        <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(4)} />
      </Collapsable>
      {isOnFinalPage && (
        <>
          {loanparts.length > 0 && (
            <Collapsable title="Leningdelen" openByDefault>
              <Seperator css={{ marginTop: '0px' }}/>
              {loanparts.map((loanpart, index) => (
                <div key={`loanpart${index}`}>
                  <LoanpartHeading>
                    {`Leningdeel ${index + 1} -
                    ${settings.meta.mortgage_types.find(type => type.value === loanpart.mortgage_type).label}
                    -
                    ${currencyFormatter(loanpart.remaining_debt)}`}
                  </LoanpartHeading>
                  <Display
                    css={{ marginLeft: '8px' }}
                    label="Rente"
                    value={percentageFormatter(loanpart.interest_rate, 0)}
                  />
                  {loanpart.variable_interest_rate ? (
                    <Display label="Variabele rente" value={loanpart.variable_interest_rate} />
                    ) : (
                    <Display
                      css={{ marginLeft: '8px' }}
                      label="Einde rentevaste periode"
                      value={loanpart.frt_end_date}
                    />
                  )}
                  <Display
                    css={{ marginLeft: '8px' }}
                    label="Einde leningdeel"
                    value={loanpart.end_date}
                  />
                  <Seperator css={{ marginTop: '16px' }}/>
                </div>
              ))}
              <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(6)} />
            </Collapsable>
          )}
          <Collapsable title="Jouw gegevens">
            <Display label="Geboortedatum" value={applicant_dob} />
            <Display label="Ondernemer" value={applicant_is_entrepreneur} />
            <Display
              label="Bruto jaarinkomen"
              value={currencyFormatter(applicant_income)}
            />
            {applicant_showPension && (
              <Display
                label="Bruto AOW-inkomen"
                value={currencyFormatter(applicant_pension_income)}
              />
            )}
            <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(7)} />
          </Collapsable>
          {applicant_has_partner && (
            <Collapsable title="Gegevens partner">
              <Display label="Geboortedatum" value={partner_dob} />
              <Display label="Ondernemer" value={partner_is_entrepreneur} />
              <Display
                label="Bruto jaarinkomen"
                value={currencyFormatter(partner_income)}
              />
              {partner_showPension && (
                <Display
                  label="Bruto AOW-inkomen"
                  value={currencyFormatter(partner_pension_income)}
                />
              )}
              <DisplayLink css={css`margin-top: 16px; `} onClick={() => setStepIndex(8)} />
            </Collapsable>
          )}
        </>
      )}
      <PreviousStepButton
        css={{ marginTop: '40px' }}
        handleClick={() => setSelectedDetailType(null)}>
        Andere mogelijkheden
      </PreviousStepButton>
    </InputContainer>
  );
};

OverviewContainer.propTypes = {
  settings: shapeSettings.isRequired,
};

export default OverviewContainer;
